import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Sparkles, Save, Send } from "lucide-react";
import { VoiceInputButton, appendTranscript } from "@/components/VoiceInputButton";

export default function CapstonePage() {
  const { projectId } = useParams();
  const [meta, setMeta] = useState(null);
  const [fields, setFields] = useState({});
  const [evidence, setEvidence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [suggestions, setSuggestions] = useState([]);   // M-W7 Stage 3: lesson artifacts
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: list }, { data: prog }] = await Promise.all([
          api.get("/courses"),
          api.get("/courses/progress"),
        ]);
        // Find capstone across all courses
        let found = null;
        let courseTitle = null;
        let courseId = null;
        for (const c of list.courses) {
          if (c.capstone_id === projectId) {
            // Need deep course
            const { data: deep } = await api.get(`/courses/${c.id}`);
            found = deep.course.capstone;
            courseTitle = deep.course.title;
            courseId = c.id;
            break;
          }
        }
        if (!found) {
          setError("Capstone project not found.");
        } else {
          setMeta({ project: found, courseTitle, capstoneFields: list.capstone_fields });
          const ev = (prog.projects || []).find((p) => p.project_id === projectId);
          setEvidence(ev);
          setFields(ev?.fields || {});
          // M-W7 Stage 3: load lesson artifacts from THIS course as
          // pre-fill SUGGESTIONS. Never auto-populates any capstone
          // field — participant confirms every field themselves.
          if (courseId) {
            try {
              const { data: sug } = await api.get(`/lesson-artifacts/capstone-suggestions/${courseId}`);
              setSuggestions(sug?.suggested_artifacts || []);
            } catch { /* soft-fail */ }
          }
        }
      } catch (err) {
        setError(extractApiError(err, "Failed to load capstone."));
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const { data } = await api.post(`/courses/projects/${projectId}/save`, { fields });
      setEvidence(data.evidence);
      toast.success("Draft saved.");
    } catch (err) {
      toast.error(extractApiError(err, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setErrors([]);
    try {
      const { data } = await api.post(`/courses/projects/${projectId}/submit`, { fields });
      if (data.valid) {
        setEvidence(data.evidence);
        toast.success("Capstone complete. This is real work evidence saved on your account.");
      } else {
        setErrors(data.errors || []);
        toast.error(data.message || "Please address the items below.");
      }
    } catch (err) {
      toast.error(extractApiError(err, "Submit failed."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppShell><div className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary" data-testid="capstone-loading">Loading capstone…</div></AppShell>
    );
  }
  if (error || !meta) {
    return (
      <AppShell>
        <div className="mx-auto max-w-3xl px-4 py-14">
          <Alert variant="destructive"><AlertDescription data-testid="capstone-error">{error || "Not found."}</AlertDescription></Alert>
          <Link to="/learning"><Button variant="ghost" className="mt-4 text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"><ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />Back to learning</Button></Link>
        </div>
      </AppShell>
    );
  }

  const isComplete = evidence?.status === "completed";

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/learning" className="text-xs text-fg-secondary hover:text-fg-primary transition-colors" data-testid="capstone-back-link">
          <ArrowLeft className="h-3 w-3 inline mr-1" aria-hidden="true" />
          Back to learning
        </Link>

        <header className="mt-4">
          <p className="text-[11px] uppercase tracking-wider text-fg-secondary flex items-center gap-2">
            <Sparkles className="h-3 w-3" aria-hidden="true" />
            Capstone · {meta.courseTitle}
          </p>
          <h1 className="mt-1 text-3xl font-heading font-medium text-fg-primary" data-testid="capstone-title-header">{meta.project.title}</h1>
          <p className="mt-3 text-sm text-fg-secondary">{meta.project.prompt}</p>
          {isComplete && (
            <p className="mt-3 inline-flex items-center gap-1 text-xs text-progress-teal" data-testid="capstone-completed-badge">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              Completed · saved as work evidence
            </p>
          )}
        </header>

        {errors.length > 0 && (
          <Alert variant="destructive" className="mt-6" data-testid="capstone-errors">
            <AlertDescription>
              <p className="font-medium">Please address the following before submitting:</p>
              <ul className="mt-2 list-disc pl-5 space-y-1">
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* M-W7 Stage 3: lesson artifacts as pre-fill SUGGESTIONS.
             Never auto-fills a capstone field — participant confirms. */}
        {suggestions.length > 0 && (
          <section data-testid="capstone-suggestions" className="mt-6 rounded-lg border border-accent-warm/30 bg-accent-warm/5 p-5">
            <p className="text-[11px] uppercase tracking-wider text-accent-warm">Lesson artifacts available for reference</p>
            <p className="mt-1 text-xs text-fg-secondary">
              You've completed lessons in this pathway that produced tangible artifacts. Click one below to view its content — you can copy any part into the fields below yourself. Nothing is auto-filled.
            </p>
            <div className="mt-3 space-y-2">
              {suggestions.map((a) => (
                <div key={a.id} data-testid={`capstone-suggestion-${a.origin_module_id}`} className="rounded-md border border-border-subtle bg-bg-base p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-sm text-fg-primary font-medium">{a.title}</p>
                    <button
                      type="button"
                      data-testid={`capstone-suggestion-${a.origin_module_id}-toggle`}
                      onClick={() => setSelectedSuggestionId((cur) => cur === a.id ? null : a.id)}
                      className="text-xs text-fg-secondary hover:text-fg-primary transition-colors"
                    >
                      {selectedSuggestionId === a.id ? "Hide details" : "Show details"}
                    </button>
                  </div>
                  {(a.feeds_capstone_fields || []).length > 0 && (
                    <p className="mt-1 text-[11px] text-fg-muted italic">
                      May be helpful for capstone fields: {a.feeds_capstone_fields.join(", ")}
                    </p>
                  )}
                  {selectedSuggestionId === a.id && a.fields && (
                    <div className="mt-2 space-y-2" data-testid={`capstone-suggestion-${a.origin_module_id}-details`}>
                      {Object.entries(a.fields).map(([name, val]) => (
                        <div key={name} className="rounded border border-border-subtle bg-bg-surface p-2">
                          <p className="text-[10px] uppercase tracking-wider text-fg-secondary">{name}</p>
                          <p className="mt-1 text-xs text-fg-primary whitespace-pre-line">{val}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-6 space-y-6">
          {meta.capstoneFields.map((f) => (
            <div key={f.name} data-testid={`capstone-field-${f.name}`}>
              <label className="text-sm text-fg-primary font-medium">{f.label}</label>
              <p className="mt-1 text-xs text-fg-secondary">{f.prompt}</p>
              <Textarea
                data-testid={`capstone-field-${f.name}-input`}
                placeholder={f.help}
                maxLength={8000}
                value={fields[f.name] || ""}
                onChange={(e) => setFields((s) => ({ ...s, [f.name]: e.target.value }))}
                className="mt-2 bg-bg-base border-border-subtle text-fg-primary min-h-32"
                disabled={isComplete}
              />
              {!isComplete && (
                <div className="mt-2">
                  <VoiceInputButton
                    fieldTestid={`capstone-field-${f.name}-voice-btn`}
                    ariaLabel={`Speak ${f.label}`}
                    onTranscript={(t) => setFields((s) => ({
                      ...s,
                      [f.name]: appendTranscript(s[f.name] || "", t),
                    }))}
                  />
                </div>
              )}
              <p className="mt-1 text-[11px] text-fg-muted italic">{f.help}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          <Button
            onClick={save}
            disabled={saving || isComplete}
            data-testid="capstone-save-btn"
            variant="outline"
            className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
          >
            <Save className="h-4 w-4 mr-2" aria-hidden="true" />
            {saving ? "Saving…" : "Save draft"}
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || isComplete}
            data-testid="capstone-submit-btn"
            className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
          >
            <Send className="h-4 w-4 mr-2" aria-hidden="true" />
            {submitting ? "Submitting…" : (isComplete ? "Already complete" : "Submit as work evidence")}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
